import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import QedaraLogo from "@/components/QedaraLogo";

export default function AcceptInvite({ token, onDone }) {
  const { acceptInvite } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setError("Linkul de invitație nu e valabil sau a fost deja folosit."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Scrie-ți numele");
    if (password.length < 6) return toast.error("Parola trebuie să aibă cel puțin 6 caractere");
    if (password !== confirm) return toast.error("Parolele nu se potrivesc");
    setLoading(true);
    try {
      await acceptInvite(token, name, password);
      toast.success("Bine ai venit în Qedara Chat");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invitația nu a putut fi acceptată");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-fg p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8"><QedaraLogo variant="full" markSize={40} /></div>

        {error ? (
          <>
            <p className="label-tiny mb-3">// INVITAȚIE</p>
            <h1 className="text-3xl font-bold tracking-tighter mb-2">Linkul nu mai e valabil.</h1>
            <p className="text-fg/60 text-sm">{error}</p>
          </>
        ) : (
          <>
            <p className="label-tiny mb-3">// INTRĂ {info?.workspace_name ? info.workspace_name.toUpperCase() : "WORKSPACE"}</p>
            <h1 className="text-3xl font-bold tracking-tighter mb-2">Setează-ți parola.</h1>
            <p className="text-fg/60 mb-8 text-sm">
              {info ? <>Intri ca <span className="text-fg">{info.email}</span>.</> : "Se încarcă invitația…"}
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div>
                <label className="label-tiny block mb-2">Numele tău</label>
                <input
                  data-testid="invite-name-input"
                  className="input-flat"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Ada Lovelace" required
                />
              </div>
              <div>
                <label className="label-tiny block mb-2">Parolă</label>
                <input
                  data-testid="invite-password-input"
                  type="password" required minLength={6}
                  className="input-flat"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="label-tiny block mb-2">Confirmă parola</label>
                <input
                  data-testid="invite-confirm-input"
                  type="password" required minLength={6}
                  className="input-flat"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
              <button
                data-testid="invite-submit-button"
                type="submit" disabled={loading || !info}
                className="btn-primary w-full flex items-center justify-center gap-2 mt-6"
              >
                {loading ? "..." : "Creează contul și intră"}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
